/**
 * Employment Verification case page — /staff-ops/employment/:sid
 *
 * This workspace is the operational template for NTN Employment Verifications.
 * Landlord Verification and Document Validation will inherit this exact
 * architecture once approved.
 *
 * Structure (per spec):
 *   1. Case Summary                         → header of VerificationWorkspaceShell + summary strip
 *   2. Application Package (pinned)         → ApplicationPackagePanel in shell
 *   3. Claimed Employment (editable)        → ClaimedEmploymentCard
 *   4. Verification Attempts (audit log)    → shell right-rail contact log
 *   5. Employment Verification Questions    → EmploymentQuestionsCard (structured)
 *   6. Supporting Document Review           → SupportingDocReviewCard
 *   7. Verification Findings                → InternalReportCard (Claimed vs Verified)
 *   8. Overall Verification Outcome         → Complete dialog (4 outcomes)
 *   9. Risk Intelligence                    → auto-generated on complete (Phase B)
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import { fmtDateLike } from "@/lib/dateFormat";
import CopyVerificationDialog from "@/components/CopyVerificationDialog";
import AppShell from "@/components/AppShell";
import { VerificationEntriesPanel } from "@/components/VerificationEntries";
import VerificationWorkspaceShell from "@/components/VerificationWorkspaceShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Briefcase, CheckCircle2, FileText, Save, ClipboardCheck, Send, Undo2, ShieldAlert } from "lucide-react";
import ManualRiskSelector from "@/components/ManualRiskSelector";
import VerifiedReport from "@/components/VerifiedReport";
import { DashboardSkeleton } from "@/components/Skeleton";
import { fmtDateTime } from "@/lib/dateFormat";

// ── Vocabularies ─────────────────────────────────────────────────────────
const EMPLOYMENT_STATUS_CLAIMED = [
  { v: "full_time", l: "Full Time" },
  { v: "part_time", l: "Part Time" },
  { v: "seasonal", l: "Seasonal" },
  { v: "temporary", l: "Temporary" },
  { v: "contract", l: "Contract" },
  { v: "self_employed", l: "Self Employed" },
];
const PAY_FREQUENCIES = [
  { v: "weekly", l: "Weekly" },
  { v: "bi_weekly", l: "Bi-weekly" },
  { v: "semi_monthly", l: "Semi-monthly" },
  { v: "monthly", l: "Monthly" },
  { v: "annual", l: "Annual" },
];
const YES_NO_UNABLE = [
  { v: "yes", l: "Yes" },
  { v: "no", l: "No" },
  { v: "unable", l: "Unable to Verify" },
];
const POSITION_MATCH = [
  { v: "exact_match", l: "Exact Match" },
  { v: "similar", l: "Similar" },
  { v: "different", l: "Different" },
  { v: "unable", l: "Unable to Verify" },
];
const CONSISTENCY = [
  { v: "consistent", l: "Consistent" },
  { v: "minor_variance", l: "Minor Variance" },
  { v: "inconsistent", l: "Inconsistent" },
  { v: "not_available", l: "Not Available" },
];
const OUTCOMES = [
  { v: "verified", l: "Verified — everything matches" },
  { v: "partially_verified", l: "Partially Verified" },
  { v: "unable_to_verify", l: "Unable to Verify" },
  { v: "discrepancy_identified", l: "Discrepancy Identified" },
];
const VERIFICATION_METHOD = [
  { v: "phone", l: "Phone (direct)" },
  { v: "email", l: "Email" },
  { v: "hr_portal", l: "HR Portal" },
  { v: "voe_letter", l: "VOE Letter" },
  { v: "paystub_review", l: "Paystub Review" },
  { v: "third_party", l: "Third-party VOE Service" },
  { v: "written", l: "Written" },
];

// ── Utilities ────────────────────────────────────────────────────────────
const labelOf = (arr, v) => arr.find((x) => x.v === v)?.l || (v ? v.replace(/_/g, " ") : "—");
const money = (n) => (n === null || n === undefined || n === "" ? "—"
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const dateVariance = (a, b) => {
  if (!a || !b) return null;
  const da = new Date(a); const db = new Date(b);
  if (isNaN(da) || isNaN(db)) return null;
  const days = Math.round((db - da) / (1000 * 60 * 60 * 24));
  if (days === 0) return "Same day";
  return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ${days > 0 ? "later" : "earlier"}`;
};
const incomeVariancePct = (claimed, verified) => {
  const c = Number(claimed); const v = Number(verified);
  if (!c || !v || isNaN(c) || isNaN(v)) return null;
  return (((v - c) / c) * 100).toFixed(1);
};

// ── Section: Claimed Employment (editable, section 3) ────────────────────
function ClaimedEmploymentCard({ sid, claimedEmployment, canEdit, onSaved }) {
  const [form, setForm] = useState(claimedEmployment || {});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setForm(claimedEmployment || {}); setDirty(false); }, [claimedEmployment]);

  const set = (k, v) => { setForm((prev) => ({ ...prev, [k]: v })); setDirty(true); };

  // Auto-calc annual whenever monthly changes and annual is empty
  const displayedAnnual = useMemo(() => {
    if (form.annual_income) return form.annual_income;
    if (form.monthly_income) return Number(form.monthly_income) * 12;
    return "";
  }, [form.monthly_income, form.annual_income]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        monthly_income: form.monthly_income ? Number(form.monthly_income) : null,
        annual_income: form.annual_income ? Number(form.annual_income) : null,
      };
      const { data } = await apiClient.put(`/staff-ops/case/${sid}/employment/claimed-employment`, payload);
      toast.success("Claimed employment saved");
      setDirty(false);
      onSaved?.(data.claimed_employment);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4" data-testid="claimed-employment-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-slate-500" />
          <div className="ntn-eyebrow text-slate-600">Claimed Employment</div>
        </div>
        {claimedEmployment?.seeded_from_application && !claimedEmployment?.saved_at && (
          <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
            Auto-populated from Rental Application — review & save
          </span>
        )}
        {claimedEmployment?.saved_at && (
          <span className="text-[11px] text-slate-500">
            Saved {fmtDateTime(claimedEmployment.saved_at)}
            {claimedEmployment.saved_by_name ? ` · ${claimedEmployment.saved_by_name}` : ""}
          </span>
        )}
      </div>

      {/* Employer Information */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Employer Information</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Employer Name</Label>
            <Input value={form.employer_name || ""} onChange={(e) => set("employer_name", e.target.value)}
                   disabled={!canEdit} data-testid="ce-employer-name" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Employer Address</Label>
            <Input value={form.employer_address || ""} onChange={(e) => set("employer_address", e.target.value)}
                   disabled={!canEdit} data-testid="ce-employer-address" />
          </div>
          <div>
            <Label className="text-xs">Employer Phone Number</Label>
            <Input value={form.employer_phone || ""} onChange={(e) => set("employer_phone", e.target.value)}
                   disabled={!canEdit} data-testid="ce-employer-phone" />
          </div>
        </div>
      </div>

      {/* Applicant Claimed Information */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Applicant Claimed Information</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Job Title</Label>
            <Input value={form.job_title || ""} onChange={(e) => set("job_title", e.target.value)}
                   disabled={!canEdit} data-testid="ce-job-title" />
          </div>
          <div>
            <Label className="text-xs">Employment Status</Label>
            <Select value={form.employment_status || ""} onValueChange={(v) => set("employment_status", v)} disabled={!canEdit}>
              <SelectTrigger data-testid="ce-employment-status"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {EMPLOYMENT_STATUS_CLAIMED.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Hire Date</Label>
            <Input type="date" value={form.hire_date || ""} onChange={(e) => set("hire_date", e.target.value)}
                   disabled={!canEdit} data-testid="ce-hire-date" />
          </div>
          <div>
            <Label className="text-xs">Supervisor (if listed)</Label>
            <Input value={form.supervisor || ""} onChange={(e) => set("supervisor", e.target.value)}
                   disabled={!canEdit} data-testid="ce-supervisor" />
          </div>
          <div>
            <Label className="text-xs">Monthly Income</Label>
            <Input type="number" step="0.01" value={form.monthly_income ?? ""}
                   onChange={(e) => set("monthly_income", e.target.value)}
                   disabled={!canEdit} data-testid="ce-monthly-income" />
          </div>
          <div>
            <Label className="text-xs">Annual Income (auto-calculated)</Label>
            <Input type="number" step="0.01" value={form.annual_income ?? displayedAnnual ?? ""}
                   onChange={(e) => set("annual_income", e.target.value)}
                   placeholder={displayedAnnual ? String(displayedAnnual) : ""}
                   disabled={!canEdit} data-testid="ce-annual-income" />
          </div>
          <div>
            <Label className="text-xs">Pay Frequency</Label>
            <Select value={form.pay_frequency || ""} onValueChange={(v) => set("pay_frequency", v)} disabled={!canEdit}>
              <SelectTrigger data-testid="ce-pay-frequency"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {PAY_FREQUENCIES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-1">
        <Button onClick={save} disabled={!canEdit || busy || !dirty}
                className="bg-[#0A192F] text-white" data-testid="ce-save">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save Claimed Employment"}
        </Button>
      </div>
    </Card>
  );
}

// ── Section: Employment Verification Questions (section 5) ───────────────
function EmploymentQuestionsCard({ findings, set, canEdit, claimedEmployment }) {
  const f = findings || {};
  const claimed = claimedEmployment || {};
  const hireVariance = dateVariance(claimed.hire_date, f.verified_hire_date);
  const incomeVar = incomeVariancePct(claimed.monthly_income, f.verified_monthly_income);

  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4" data-testid="employment-questions-card">
      <div className="ntn-eyebrow text-slate-600">Employment Verification Questions</div>

      {/* Employment Status */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Employment Status</div>
        <div className="grid grid-cols-3 gap-3 items-center">
          <Label className="text-sm text-slate-700">Currently Employed?</Label>
          <Select value={f.currently_employed || ""} onValueChange={(v) => set("currently_employed", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-currently-employed"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{YES_NO_UNABLE.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Employment Type */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Employment Type</div>
        <div className="grid grid-cols-3 gap-3 items-center">
          <Label className="text-sm text-slate-700">Verified Type</Label>
          <Select value={f.employment_type_verified || ""} onValueChange={(v) => set("employment_type_verified", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-employment-type"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{EMPLOYMENT_STATUS_CLAIMED.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Position */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Position</div>
        <div className="grid grid-cols-3 gap-3 items-center">
          <Label className="text-sm text-slate-700">Position Confirmed?</Label>
          <Select value={f.position_confirmed || ""} onValueChange={(v) => set("position_confirmed", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-position-confirmed"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{POSITION_MATCH.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          <Label className="text-sm text-slate-700">Verified Job Title</Label>
          <Input className="col-span-2" value={f.verified_job_title || ""} onChange={(e) => set("verified_job_title", e.target.value)}
                 disabled={!canEdit} data-testid="q-verified-job-title" />
        </div>
      </div>

      {/* Employment Dates */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Employment Dates</div>
        <div className="grid grid-cols-3 gap-3 items-center">
          <Label className="text-sm text-slate-700">Claimed Hire Date</Label>
          <div className="col-span-2 text-sm text-slate-900">{fmtDateLike(claimed.hire_date)}</div>
          <Label className="text-sm text-slate-700">Verified Hire Date</Label>
          <Input type="date" className="col-span-2" value={f.verified_hire_date || ""}
                 onChange={(e) => set("verified_hire_date", e.target.value)}
                 disabled={!canEdit} data-testid="q-verified-hire-date" />
          <Label className="text-sm text-slate-700">Variance</Label>
          <div className="col-span-2 text-sm text-slate-700" data-testid="q-hire-variance">{hireVariance || "—"}</div>
        </div>
      </div>

      {/* Income Verification */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Income Verification</div>
        <div className="grid grid-cols-3 gap-3 items-center">
          <Label className="text-sm text-slate-700">Claimed Monthly Income</Label>
          <div className="col-span-2 text-sm text-slate-900">{money(claimed.monthly_income)}</div>
          <Label className="text-sm text-slate-700">Verified Monthly Income</Label>
          <Input type="number" step="0.01" className="col-span-2" value={f.verified_monthly_income ?? ""}
                 onChange={(e) => set("verified_monthly_income", e.target.value)}
                 disabled={!canEdit} data-testid="q-verified-income" />
          <Label className="text-sm text-slate-700">Income Type</Label>
          <Select value={f.income_type || ""} onValueChange={(v) => set("income_type", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-income-type"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="salary">Salary</SelectItem>
              <SelectItem value="hourly">Hourly</SelectItem>
              <SelectItem value="commission">Commission</SelectItem>
              <SelectItem value="tips">Tips</SelectItem>
              <SelectItem value="mixed">Mixed</SelectItem>
              <SelectItem value="self_employed">Self-employed / Draw</SelectItem>
            </SelectContent>
          </Select>
          <Label className="text-sm text-slate-700">Variance %</Label>
          <div className="col-span-2 text-sm text-slate-700" data-testid="q-income-variance">
            {incomeVar !== null ? `${incomeVar}%` : "—"}
          </div>
        </div>
      </div>

      {/* Employment Standing (spec: keep only 3) */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Employment Standing</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <Checkbox id="es-active" checked={!!f.active_employee} onCheckedChange={(v) => set("active_employee", !!v)} disabled={!canEdit} data-testid="q-active-employee" />
            <Label htmlFor="es-active" className="text-sm text-slate-700">Active Employee?</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="es-leave" checked={!!f.currently_on_leave} onCheckedChange={(v) => set("currently_on_leave", !!v)} disabled={!canEdit} data-testid="q-on-leave" />
            <Label htmlFor="es-leave" className="text-sm text-slate-700">Currently on Leave?</Label>
          </div>
          <div className="flex items-center gap-2 col-span-2">
            <Checkbox id="es-continue" checked={!!f.expected_to_continue} onCheckedChange={(v) => set("expected_to_continue", !!v)} disabled={!canEdit} data-testid="q-expected-continue" />
            <Label htmlFor="es-continue" className="text-sm text-slate-700">Expected to Continue Employment?</Label>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Section: Supporting Document Review (section 6) ──────────────────────
function SupportingDocReviewCard({ findings, set, canEdit }) {
  const f = findings.doc_review || {};
  const upd = (k, v) => set("doc_review", { ...f, [k]: v });
  const ROWS = [
    { k: "rental_app", l: "Rental Application" },
    { k: "pay_stubs", l: "Pay Stubs" },
    { k: "w2", l: "W-2" },
    { k: "employment_letter", l: "Employment Letter" },
    { k: "employer_verification", l: "Employer Verification" },
  ];
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-3" data-testid="doc-review-card">
      <div className="ntn-eyebrow text-slate-600">Supporting Document Review</div>
      <div className="text-xs text-slate-500 -mt-1">
        Confirm consistency across the applicant&apos;s supporting documents. Discrepancies feed Risk Intelligence.
      </div>
      <div className="space-y-2">
        {ROWS.map((row) => (
          <div key={row.k} className="grid grid-cols-3 gap-3 items-center">
            <Label className="text-sm text-slate-700">{row.l}</Label>
            <Select value={f[row.k] || ""} onValueChange={(v) => upd(row.k, v)} disabled={!canEdit}>
              <SelectTrigger className="col-span-2" data-testid={`docreview-${row.k}`}>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {CONSISTENCY.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <div>
        <Label className="text-xs">Reviewer notes</Label>
        <Textarea rows={2} value={f.notes || ""} onChange={(e) => upd("notes", e.target.value)}
                  disabled={!canEdit} data-testid="docreview-notes" />
      </div>
    </Card>
  );
}

// ── Section: Verification Contact used (feeds Internal Report) ───────────
function VerificationContactCard({ findings, set, canEdit }) {
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-3" data-testid="verif-contact-card">
      <div className="ntn-eyebrow text-slate-600">Verification Contact</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Contact Name</Label>
          <Input value={findings.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)}
                 disabled={!canEdit} data-testid="verif-contact-name" />
        </div>
        <div>
          <Label className="text-xs">Contact Title</Label>
          <Input value={findings.contact_title || ""} onChange={(e) => set("contact_title", e.target.value)}
                 disabled={!canEdit} data-testid="verif-contact-title" />
        </div>
        <div>
          <Label className="text-xs">Phone</Label>
          <Input value={findings.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)}
                 disabled={!canEdit} data-testid="verif-contact-phone" />
        </div>
        <div>
          <Label className="text-xs">Verification Date</Label>
          <Input type="date" value={findings.verification_date || ""}
                 onChange={(e) => set("verification_date", e.target.value)}
                 disabled={!canEdit} data-testid="verif-date" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Verification Method</Label>
          <Select value={findings.method || ""} onValueChange={(v) => set("method", v)} disabled={!canEdit}>
            <SelectTrigger data-testid="verif-method"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {VERIFICATION_METHOD.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Internal Notes · NTN only</Label>
          <Textarea rows={3} value={findings.internal_notes || ""}
                    onChange={(e) => set("internal_notes", e.target.value)}
                    disabled={!canEdit} data-testid="verif-internal-notes" />
        </div>
      </div>
    </Card>
  );
}

// ── Section: Internal Verification Report (section 7 — Claimed vs Verified)
function InternalReportCard({ claimedEmployment, findings }) {
  const c = claimedEmployment || {};
  const f = findings || {};
  const rows = [
    { l: "Employer Name", claimed: c.employer_name, verified: f.verified_employer_name || c.employer_name },
    { l: "Employment Status", claimed: labelOf(EMPLOYMENT_STATUS_CLAIMED, c.employment_status), verified: labelOf(EMPLOYMENT_STATUS_CLAIMED, f.employment_type_verified) },
    { l: "Job Title", claimed: c.job_title, verified: f.verified_job_title },
    { l: "Hire Date", claimed: fmtDateLike(c.hire_date), verified: fmtDateLike(f.verified_hire_date) },
    { l: "Monthly Income", claimed: money(c.monthly_income), verified: money(f.verified_monthly_income) },
    { l: "Employment Type", claimed: labelOf(EMPLOYMENT_STATUS_CLAIMED, c.employment_status), verified: labelOf(EMPLOYMENT_STATUS_CLAIMED, f.employment_type_verified) },
  ];
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-3" data-testid="internal-report-card">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4 text-slate-500" />
        <div className="ntn-eyebrow text-slate-600">Internal Verification Report (Preview)</div>
      </div>
      <div className="text-xs text-slate-500 -mt-1">
        Complete Employment Verification report retained by NTN. Not shown to the Property Manager.
      </div>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-3 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 px-3 py-2">
          <div>Field</div><div>Claimed</div><div>Verified</div>
        </div>
        {rows.map((r) => (
          <div key={r.l} className="grid grid-cols-3 px-3 py-2 text-sm border-t border-slate-100">
            <div className="text-slate-700 font-medium">{r.l}</div>
            <div className="text-slate-900">{r.claimed || "—"}</div>
            <div className="text-slate-900">{r.verified || "—"}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm text-slate-700">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Verification Contact</div>
          <div>{f.contact_name || "—"}{f.contact_title ? `, ${f.contact_title}` : ""}</div>
          <div className="text-slate-500 text-xs">{f.contact_phone || ""}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Method / Date</div>
          <div>{labelOf(VERIFICATION_METHOD, f.method)}</div>
          <div className="text-slate-500 text-xs">{f.verification_date ? fmtDateLike(f.verification_date) : ""}</div>
        </div>
      </div>
    </Card>
  );
}

// ── Section: PM Summary Preview + Publish (Phase B) ──────────────────────
function PMSummaryCard({ sid, verification, canEdit, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const isCompleted = verification?.status === "completed" && !!verification?.outcome;
  const published = verification?.pm_summary;
  const publishedAt = published?.published_at;
  const manualBanner = verification?.manual_status_banner || null;

  const loadPreview = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/staff-ops/case/${sid}/employment/summary-preview`);
      setPreview(data.preview);
    } catch (e) { /* silent — preview is best-effort */ }
  }, [sid]);

  useEffect(() => { if (isCompleted) loadPreview(); }, [isCompleted, loadPreview, verification]);

  const publish = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/staff-ops/case/${sid}/employment/publish-summary`);
      toast.success("Property Manager summary published");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Publish failed"); }
    finally { setBusy(false); }
  };
  const unpublish = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/staff-ops/case/${sid}/employment/unpublish-summary`);
      toast.success("Property Manager summary rescinded");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Unpublish failed"); }
    finally { setBusy(false); }
  };

  const source = published || preview;
  // Normalize bullets → value so the shared VerifiedReport can render them
  const reportPayload = source ? {
    ...source,
    bullets: (source.bullets || []).map((b) => ({ ...b, value: b.verified || b.claimed || "—" })),
    risk_indicators: source.risk_indicators || [],
  } : null;

  return (
    <div className="space-y-3" data-testid="pm-summary-card">
      {/* Action bar */}
      <Card className="p-4 bg-white border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-slate-500" />
          <div>
            <div className="ntn-eyebrow text-slate-600">Property Manager Report</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {publishedAt
                ? <>Published <span data-testid="pm-summary-published-badge">{fmtDateTime(publishedAt)}</span> · {published.published_by_name}</>
                : (isCompleted
                    ? "Preview only — not visible to the Property Manager until published."
                    : "Complete verification to enable publish.")}
            </div>
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {/* Phase E.1: Manual PM risk selector — required before Publish */}
          {manualBanner ? (
            <span
              className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-sm ring-1 ${
                manualBanner.tone === "green"  ? "bg-emerald-50 text-emerald-800 ring-emerald-200" :
                manualBanner.tone === "amber"  ? "bg-amber-50 text-amber-800 ring-amber-200" :
                manualBanner.tone === "orange" ? "bg-orange-50 text-orange-800 ring-orange-200" :
                                                  "bg-rose-50 text-rose-800 ring-rose-200"
              }`}
              data-testid="manual-risk-chip"
            >{manualBanner.label}</span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-sm ring-1 bg-slate-50 text-slate-700 ring-slate-200"
                  data-testid="manual-risk-chip">Risk not set</span>
          )}
          <Button variant="outline" onClick={() => setRiskOpen(true)} disabled={!canEdit || !isCompleted}
                  className="rounded-md" data-testid="manual-risk-btn">
            <ShieldAlert className="h-4 w-4 mr-2" /> {manualBanner ? "Edit risk" : "Set risk"}
          </Button>
          {publishedAt ? (
            <Button variant="outline" onClick={unpublish} disabled={!canEdit || busy}
                    className="rounded-md" data-testid="pm-unpublish-btn">
              <Undo2 className="h-4 w-4 mr-2" /> Rescind
            </Button>
          ) : null}
          <Button onClick={publish} disabled={!canEdit || busy || !isCompleted || !manualBanner}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md"
                  data-testid="pm-publish-btn"
                  title={!manualBanner ? "Set a risk banner before publishing" : ""}>
            <Send className="h-4 w-4 mr-2" /> {publishedAt ? "Re-publish" : "Publish to PM"}
          </Button>
        </div>
      </Card>

      <ManualRiskSelector
        open={riskOpen}
        onOpenChange={setRiskOpen}
        track="employment"
        screeningId={sid}
        current={manualBanner}
        onSaved={() => onChanged?.()}
      />

      {/* Report preview — this is EXACTLY what the PM will see once published */}
      {reportPayload ? (
        <VerifiedReport
          moduleName="Employment Verification"
          title="Verified Employment Report"
          Icon={Briefcase}
          payload={reportPayload}
          testid="pm-summary-preview-report"
          sectionLabel="Verified Employment Information"
        />
      ) : (
        <Card className="p-6 bg-white border-slate-200 text-xs text-slate-400 italic">
          Report will appear here once the verification is completed.
        </Card>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────
export default function EmploymentVerificationPage() {
  const { sid } = useParams();
  const [workspace, setWorkspace] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [findings, setFindings] = useState({});
  const [completeOpen, setCompleteOpen] = useState(false);
  const [outcome, setOutcome] = useState("verified");
  const [summary, setSummary] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, s] = await Promise.all([
        apiClient.get(`/staff-ops/case/${sid}/employment`),
        apiClient.get(`/staff-ops/staff`),
      ]);
      setWorkspace(w.data);
      setStaff(s.data);
      setFindings(w.data.verification?.findings || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  if (loading || !workspace) return <AppShell><DashboardSkeleton /></AppShell>;

  const canEdit = workspace.can_edit || workspace.supervisor_override_available;
  const claimedEmployment = workspace.verification?.claimed_employment || {};

  const set = (k, v) => setFindings((prev) => ({ ...prev, [k]: v }));

  const saveDraft = async () => {
    try {
      await apiClient.post(`/staff-ops/case/${sid}/employment/save-draft`, { findings });
      toast.success("Findings draft saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };

  const submitComplete = async () => {
    try {
      await apiClient.post(`/staff-ops/case/${sid}/employment/complete`, {
        outcome, findings, summary,
      });
      toast.success(`Verification completed · ${outcome.replace(/_/g, " ")}`);
      setCompleteOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Complete failed"); }
  };

  return (
    <AppShell>
      <VerificationWorkspaceShell
        track="employment"
        sid={sid}
        workspace={workspace}
        staff={staff}
        reload={load}
        onSaveDraft={saveDraft}
        onComplete={() => setCompleteOpen(true)}
      >
        {/* 3. Claimed Employment (editable) */}
        <ClaimedEmploymentCard
          sid={sid}
          claimedEmployment={claimedEmployment}
          canEdit={canEdit}
          onSaved={load}
        />

        {/* v1.0.8 — additional employer verification entries */}
        <VerificationEntriesPanel sid={sid} track="employment" />

        {/* 5. Employment Verification Questions */}
        <EmploymentQuestionsCard
          findings={findings}
          set={set}
          canEdit={canEdit}
          claimedEmployment={claimedEmployment}
        />

        {/* 6. Supporting Document Review */}
        <SupportingDocReviewCard findings={findings} set={set} canEdit={canEdit} />

        {/* Verification contact meta (feeds Internal Report) */}
        <VerificationContactCard findings={findings} set={set} canEdit={canEdit} />

        {/* 7. Internal Verification Report (Preview) */}
        <InternalReportCard claimedEmployment={claimedEmployment} findings={findings} />

        {/* v1.0.10 — copy completed verification to active co-applicants */}
        {workspace.verification?.copied_from && (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2"
               data-testid="copied-from-note">
            Copied from {workspace.verification.copied_from.source_applicant_name}&rsquo;s employment
            verification on {fmtDateLike(workspace.verification.copied_from.copied_at?.slice(0, 10))} by{" "}
            {workspace.verification.copied_from.copied_by_name}.
          </div>
        )}
        <CopyVerificationDialog sid={sid} track="employment"
                                sourceName={workspace.screening?.applicant_name || "this applicant"}
                                onCopied={load} />

        {/* Phase B: Property Manager Summary + Publish action */}
        <PMSummaryCard
          sid={sid}
          verification={workspace.verification}
          canEdit={canEdit}
          onChanged={load}
        />
      </VerificationWorkspaceShell>

      {/* 8. Overall Verification Outcome — Complete dialog */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="max-w-lg" data-testid="complete-verification-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Complete verification</DialogTitle>
            <DialogDescription>
              Pick the overall outcome. Risk Intelligence is updated automatically from your structured findings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Outcome</Label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger data-testid="complete-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OUTCOMES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Summary (optional)</Label>
              <Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="complete-summary" />
            </div>
            <div className="text-xs text-slate-500 flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md p-2">
              <FileText className="h-3.5 w-3.5 mt-0.5" />
              Risk Intelligence indicators are auto-generated from your structured findings on completion. The Property Manager Summary is published separately via the &quot;Publish to PM&quot; action.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancel</Button>
            <Button onClick={submitComplete} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="complete-submit">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
